import type {
  CreateObjectDynamicField,
  CreateObjectDynamicFieldGroup,
  SaveCreateObjectDynamicFieldsRequest,
} from 'shared/api/kyrgyzstan/create-object'

export type CreateObjectDynamicFieldValues = Record<number, string>

export const hydrateCreateObjectDynamicFieldValues = (
  fieldGroups: CreateObjectDynamicFieldGroup[],
): CreateObjectDynamicFieldValues =>
  fieldGroups.reduce<CreateObjectDynamicFieldValues>((acc, fieldGroup) => {
    fieldGroup.fields.forEach((field) => {
      if (field.value === undefined || field.value === null) {
        return
      }

      acc[field.id] = String(field.value)
    })

    return acc
  }, {})

export const serializeCreateObjectDynamicFieldValues = (
  values: CreateObjectDynamicFieldValues,
  fieldGroups: CreateObjectDynamicFieldGroup[],
): SaveCreateObjectDynamicFieldsRequest => {
  const fields = fieldGroups.flatMap((fieldGroup) =>
    fieldGroup.fields.flatMap((field) => {
      const value = values[field.id]

      if (field.field_type === 'boolean') {
        return value === 'true'
          ? [
              {
                id: field.id,
                value,
              },
            ]
          : []
      }

      const trimmedValue = value?.trim() ?? ''

      return trimmedValue
        ? [
            {
              id: field.id,
              value: trimmedValue,
            },
          ]
        : []
    }),
  )

  return {
    fields,
  }
}

export const areCreateObjectDynamicFieldValuesEqual = (
  leftValues: CreateObjectDynamicFieldValues,
  rightValues: CreateObjectDynamicFieldValues,
  fieldGroups: CreateObjectDynamicFieldGroup[],
) =>
  JSON.stringify(
    serializeCreateObjectDynamicFieldValues(leftValues, fieldGroups),
  ) ===
  JSON.stringify(
    serializeCreateObjectDynamicFieldValues(rightValues, fieldGroups),
  )

export const getCreateObjectMainAmenityFields = (
  fieldGroups: CreateObjectDynamicFieldGroup[],
): CreateObjectDynamicField[] =>
  fieldGroups[0]?.fields.filter((field) => field.field_type === 'boolean') ?? []

export const getCreateObjectAdditionalAmenitiesSelectedCount = (
  fieldGroups: CreateObjectDynamicFieldGroup[],
  values: CreateObjectDynamicFieldValues,
): number =>
  fieldGroups.reduce((count, fieldGroup) => {
    const selectedFieldsCount = fieldGroup.fields.filter((field) => {
      if (field.field_type === 'boolean') {
        return false
      }

      const value = values[field.id]?.trim()

      if (!value) {
        return false
      }

      if (field.field_type === 'number') {
        return Number(value) > 0
      }

      return true
    }).length

    return count + selectedFieldsCount
  }, 0)
